import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select, 
    Tooltip,
} from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { 
    sortMultipleFields,     
    filterCrewMember, 
} from 'state/helpers';
import { fetchCrewTotalHours } from 'state/api';
import { countCrewTotalHours } from './utils';
import Header from 'components/Header';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import NumberControl from 'components/NumberControl';
import WorkloadStatsTable from 'components/WorkloadStatsTable';
import ChartDialog from 'components/chartNivo/ChartDialog';
import CrewFilterDialog from 'components/CrewFilterDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import { boxStyles as styles } from 'components/CustomStyles';


const viewOptions = [
  { value: "bhByPostBase", label: "by Rank & Base" },
  { value: "bhByPost", label: "by Rank" },
  { value: "bhByBase", label: "by Base" },
];

const histogramOption = { value: "histogram", label: "Histogram" };

const displayOptions = [
    { value: "charts", label: "Charts" },
    { value: "stats",  label: "Stats" },
];

const CrewTotalHoursChart = () => {    
    useAuthCheck();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();    

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };
    
    const [view, setView] = useState('bhByPostBase');
    const [displayType, setDisplayType] = useState('charts');
    const [crewBlockHours, setCrewBlockHours] = useState([]); 
    const [minTotalHours, setMinTotalHours] = useState(0); 
    const [data, setData] = useState({
        bhByPostBase: [],
        stackedBHByPostBase: [],
        bhByPost: [],    
        stackedBHByPost: [],
        bhByBase: [],
        stackedBHByBase: [],
        histogram: [],
    });
       
    const crewRanksData = useSelector((state) => state.crewRanks);

    useEffect(() => {
        const handleCrewTotalHours = async () => {
            if (!startDate || !endDate) return [];
            
            try {
                setError(null);
                setLoading(true);

                const { data } = await fetchCrewTotalHours(startDate, endDate);
                if (data) {        
                    const sortedCrewTotalHours = sortMultipleFields(data, [
                        { field: 'post', direction: 'asc' }, 
                        { field: 'base', direction: 'asc' },
                        { field: 'totalHours', direction: 'desc' },
                        { field: 'sectors', direction: 'desc' },
                    ]).filter(member => member.totalHours > 0); 
        
                    setCrewBlockHours(sortedCrewTotalHours);
                }
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false); 
            }
        };

        handleCrewTotalHours();
    }, [startDate, endDate]); 

    const filteredCrewBlockHours = useMemo(() => {
        if (!Array.isArray(crewBlockHours)) {
            return []; 
        }
    
        if (!filters) return crewBlockHours; 
    
        return crewBlockHours.filter(member => filterCrewMember(member, filters));
    }, [crewBlockHours, filters]);

    useEffect(() => {        
        const results = {
            bhByPostBase: countCrewTotalHours(filteredCrewBlockHours, 'postbase', minTotalHours), 
            stackedBHByPostBase: countCrewTotalHours(filteredCrewBlockHours, 'postbase', minTotalHours, 'stack'), 
            bhByPost: countCrewTotalHours(filteredCrewBlockHours, 'post', minTotalHours), 
            stackedBHByPost: countCrewTotalHours(filteredCrewBlockHours, 'post', minTotalHours, 'stack'), 
            bhByBase: countCrewTotalHours(filteredCrewBlockHours, 'base', minTotalHours),
            stackedBHByBase: countCrewTotalHours(filteredCrewBlockHours, 'base', minTotalHours, 'stack'),
            histogram: countCrewTotalHours(filteredCrewBlockHours, 'all', minTotalHours, 'histogram'),
        };                
        setData(results);
    }, [filteredCrewBlockHours, minTotalHours]); 
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };  

    const baseOptions = [...new Set(crewBlockHours.map(member => member.base))].sort();
    const postOptions = [...new Set(crewBlockHours.map(member => member.post))]
        .map(post => crewRanksData.find(rank => rank.rankCode === post)) 
        .filter(Boolean) // Remove undefined values
        .sort((a, b) => a.rankPriority - b.rankPriority) 
        .map(rank => rank.rankCode);
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Crew Total Hours ${period}`;
    
    const chartMap = {
        bhByPostBase: [
            { component: <SortBarMultiLinesChart data={data.bhByPostBase} title={defaultTitle} bottomLegend='Rank & Base' leftLegend='Hours' digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortStackedBarChart data={data.stackedBHByPostBase} title={defaultTitle} bottomLegend='Rank & Base' leftLegend='Hours' digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
        ],  
        bhByPost: [
            { component: <SortBarMultiLinesChart data={data.bhByPost} title={defaultTitle} bottomLegend='Rank' leftLegend='Hours' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortStackedBarChart data={data.stackedBHByPost} title={defaultTitle} bottomLegend='Rank' leftLegend='Hours' isHorizontalBottomLegend={true} digit={1} bottomOffSet={-30} bottomLegendOffSet={-30} />, style: {} },
        ],        
        bhByBase: [
            { component: <SortBarMultiLinesChart data={data.bhByBase} title={defaultTitle} bottomLegend='Base' leftLegend='Hours' digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortStackedBarChart data={data.stackedBHByBase} title={defaultTitle} bottomLegend='Base' leftLegend='Hours' digit={1} bottomOffSet={10} bottomLegendOffSet={10} />, style: {} },
        ],
        histogram: [
            { component: <SortBarChart data={data.histogram} title={defaultTitle} bottomLegend='Total Hours' leftLegend='No. of Crew' isShowAvgLine={false} digit={1} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],
    }; 
    
    const renderCharts = (charts, customStyles) => (
        charts.map((chart, index) => (
            <Box
                key={index}
                onDoubleClick={() => handleChartOpen(chart)}
                sx={{ ...styles, ...customStyles }}
            >
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>
            </Box>
        ))
    );

    const calcStats = (values) => {
        if (!values || values.length === 0) return null;

        // Filter value > 0
        const filtered = values.filter(v => v > 0);
        if (filtered.length === 0) return null;

        const n = filtered.length;
        const sum = filtered.reduce((a, b) => a + b, 0);
        const mean = sum / n;

        // Standard Deviation (Population)
        const variance = filtered.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / n;
        const stdDev = Math.sqrt(variance);

        const max = Math.max(...filtered);
        const min = Math.min(...filtered);
        const gap = max - min;

        // Workload Equity Index (1 - stdDev/mean) → càng gần 1 càng cân bằng
        const equityIndex = mean > 0 ? 1 - stdDev / mean : 0;

        // Fairness Index (min / max) → càng gần 1 càng công bằng
        const fairnessIndex = max > 0 ? min / max : 0;

        return { mean, stdDev, gap, equityIndex, fairnessIndex };
    };
    
    const calcWorkLoadStats = useCallback((data, groupBy, minValue) => {
        const groups = data.reduce((acc, cur) => {
            let key;
            switch (groupBy) {
                case 'post': 
                    key = cur.post; 
                    break;
                case 'base': 
                    key = cur.base; 
                    break;
                case 'postbase': 
                    key = `${cur.post} ${cur.base}`; 
                    break;
                default: 
                    key = cur.crewCode; 
            }

            if (!acc[key]) {
                acc[key] = { values: [], 
                    members: [], 
                };
            }

            const val = cur.totalHours || 0;
            if (val > minValue) {
                acc[key].values.push(val);
            }
            acc[key].members.push(cur);

            return acc;
        }, {});

        const workloadStats = [];
        for (const [key, val] of Object.entries(groups)) {
            const stats = calcStats(val.values);
            if (stats) {
                workloadStats.push({
                    group: key,
                    avgTotalHours: stats.mean,
                    stdDevTotalHours: stats.stdDev,
                    maxMinGap: stats.gap,
                    equityIndex: stats.equityIndex,
                    fairnessIndex: stats.fairnessIndex
                });
            }
        }

        return workloadStats;
    }, []);

    const statsData = useMemo(() => {
        if (!filteredCrewBlockHours || filteredCrewBlockHours.length === 0) return [];
        switch (view) {
            case "bhByPostBase":
            return calcWorkLoadStats(filteredCrewBlockHours, "postbase", minTotalHours);
            case "bhByPost":
            return calcWorkLoadStats(filteredCrewBlockHours, "post", minTotalHours);
            case "bhByBase":
            return calcWorkLoadStats(filteredCrewBlockHours, "base", minTotalHours);
            default:
            return [];
        }
    }, [view, filteredCrewBlockHours, calcWorkLoadStats, minTotalHours]);


    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header 
                    title={!isMobile ? "Crew Total Hours" : "Crew BH"}
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        {!isMobile && (       
                            <Tooltip title="Minimum Total Hours" arrow>
                                <span>
                                    <NumberControl
                                        min={0}
                                        max={99999}
                                        value={minTotalHours}
                                        onChange={(value) => { setMinTotalHours(value) }} 
                                        textFieldWidth={45}
                                    />
                                </span>
                            </Tooltip>
                        )}
                        
                        {!isMobile && (
                            <FormControl 
                                size="small" 
                                sx={{ width: isMobile ? "6rem" : "8rem" }}
                            >
                                <InputLabel>Display</InputLabel>
                                <Select
                                    value={displayType}
                                    label="Display"
                                    onChange={(e) => setDisplayType(e.target.value)}
                                >
                                    {displayOptions.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '6rem', sm: '10rem' } }}
                        >
                            <InputLabel id="bh-view-label">View</InputLabel>
                            <Select
                                labelId="bh-view-label"
                                label="View"
                                value={view}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}

                                {displayType === 'charts' && (
                                    <MenuItem value={histogramOption.value}>
                                        {histogramOption.label}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    <Box 
                        ref={scrollRef}
                        height={LAYOUT_HEIGHT}  
                        display="flex"
                        flexDirection="column"
                        gap={2}
                        sx={{ gridColumn: "span 12", overflowY: 'auto' }}                            
                    >
                        {displayType === "charts" ? (
                            view in chartMap &&
                                renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })
                        ) : (
                            <WorkloadStatsTable stats={statsData} />
                        )}
                    </Box>

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
       
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}

            {isFilterDialogOpen &&
                <CrewFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    postOptions={postOptions}
                    baseOptions={baseOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default CrewTotalHoursChart;
